import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert';
import useInvoice from 'hooks/invoice/useInvoice';
import useReopenInvoice from 'hooks/invoice/useReopenInvoice';
import notificationHandler from 'utils/notifications';

import InvoiceSummaryCard from './InvoiceSummaryCard';

interface InvoiceReopenModalProps {
  invoiceId: string;
  opened: boolean;
  close: () => void;
}

const InvoiceReopenModal = ({ invoiceId, opened, close }: InvoiceReopenModalProps): JSX.Element => {
  const reopenInvoiceMutation = useReopenInvoice();

  const { errorNotification, successNotification } = notificationHandler();

  const { data, isLoading, isSuccess } = useInvoice(invoiceId);

  const handleReopen = (): void => {
    if (data?.data) {
      reopenInvoiceMutation.mutate(invoiceId, {
        onError: error => {
          errorNotification('Invoice failed to be reopened', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Invoice was successful reopened');
          close();
        },
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<Title order={4}>Reopen Invoice</Title>}
      trapFocus
      centered
      size='lg'
    >
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      <Stack px='md' w='100%' align='center'>
        {isSuccess && data?.data && <InvoiceSummaryCard invoice={data.data} />}
        <Text fw={500} py='sm'>
          Are you sure you want to reopen this invoice?
        </Text>
        <KiloAlert variantStyle='warning'>
          This invoice will not be collected automatically. You can edit this invoice once it is reopened.
        </KiloAlert>
      </Stack>
      <Group position='right' pt='md'>
        <Button onClick={handleReopen} disabled={isLoading} loading={reopenInvoiceMutation.isLoading}>
          Reopen
        </Button>
      </Group>
    </Modal>
  );
};

export default InvoiceReopenModal;
